/**
 * TabContainer strict validators: only the members doc/classes/TabContainer.xml lists without
 * `overrides=`, since the base-walk delivers the rest. Container has none, so this imports Control's.
 * `tab_<i>/*` (tab_container.cpp:1270-1276) is a separate `PropertyListHelper` family from TabBar's,
 * with `hidden` and no `tooltip` (propertyListRouteCoverage.test.ts).
 */
import '../control/linterParser.js';
import { validatorRegistry } from '../../../../linter/ValidatorRegistry.js';
import { v } from '../../../../linter/validators/index.js';
import { FOCUS_MODE_HINT_NAMES } from '../../../../godot/scene/control.js';
import { TAB_BAR_ALIGNMENT_HINT_NAMES, TAB_CONTAINER_TABS_POSITION_NAMES, } from '../../../../godot/scene/tabs.js';
import { propertyListFamilyEntry } from '../../../../linter/validators/indexedFamily.js';
import { TAB_CONTAINER_TABS } from '../../../../godot/classdb/propertyListFamilies.js';
/** The `tab_<idx>/<leaf>` leaves: the four `register_property` calls at tab_container.cpp:1272-1275. */
const TAB_LEAVES = {
    // tab_container.cpp:1272, Variant::STRING, no hint. set_tab_title
    // (tab_container.cpp:883) checks the tab control, never the text.
    title: v.quotedString('title'),
    // tab_container.cpp:1273, Variant::OBJECT, PROPERTY_HINT_RESOURCE_TYPE "Texture2D".
    icon: v.resourceReference('icon'),
    // tab_container.cpp:1274, Variant::BOOL, no hint.
    disabled: v.boolean('disabled'),
    // tab_container.cpp:1275, Variant::BOOL, no hint.
    hidden: v.boolean('hidden'),
};
const tabFamilyEntry = propertyListFamilyEntry(TAB_CONTAINER_TABS, {
    leaves: TAB_LEAVES,
    unknownCode: 'INVALID_TABCONTAINER_TAB_KEY',
    describes: 'tab',
    negativeIndex: {
        cite: 'property_list_helper.cpp:58',
        code: 'INVALID_TABCONTAINER_TAB_INDEX',
        message: (index) => `Tab index ${index} must be non-negative. TabContainer routes every tab_<idx>/<leaf> write through PropertyListHelper::_get_property, which returns nullptr for a negative index (property_list_helper.cpp:58), so TabContainer's own _set reports the key as unhandled and the value is silently dropped`,
    },
});
validatorRegistry.registerAll('TabContainer', tabFamilyEntry, {
    // tab_container.cpp:1208, PROPERTY_HINT_ENUM "Left,Center,Right". TabBar::set_tab_alignment's
    // `ERR_FAIL_INDEX(p_alignment, ALIGNMENT_MAX)` (tab_bar.cpp:1671, ALIGNMENT_MAX=3) agrees with the hint.
    tab_alignment: v.enumInt('tab_alignment', TAB_BAR_ALIGNMENT_HINT_NAMES, { enforced: 'tab_bar.cpp:1671' }),
    // tab_container.cpp:1209, PROPERTY_HINT_RANGE "-1,4096,1". Outside the tree, set_current_tab
    // defers (tab_container.cpp:744) to ENTER_TREE (tab_container.cpp:222-224) and TabBar::set_current_tab,
    // whose `ERR_FAIL_INDEX` (tab_bar.cpp:804) refuses below -1. -1 means no tab (tab_bar.cpp:798).
    // 4096 is only in the hint, so it warns.
    current_tab: v.int('current_tab', {
        min: -1,
        max: 4096,
        enforced: { min: 'tab_bar.cpp:804' },
        hinted: { max: 'tab_container.cpp:1209' },
    }),
    // tab_container.cpp:1210, PROPERTY_HINT_ENUM "Top,Bottom". set_tabs_position's
    // `ERR_FAIL_INDEX(p_tabs_position, POSITION_MAX)` (tab_container.cpp:820, POSITION_MAX=2) agrees.
    tabs_position: v.enumInt('tabs_position', TAB_CONTAINER_TABS_POSITION_NAMES, {
        enforced: 'tab_container.cpp:820',
    }),
    // tab_container.cpp:1211, plain BOOL, no hint. set_clip_tabs bare-assigns.
    clip_tabs: v.boolean('clip_tabs'),
    // tab_container.cpp:1212, plain BOOL, no hint. set_tabs_visible bare-assigns.
    tabs_visible: v.boolean('tabs_visible'),
    // tab_container.cpp:1213, plain BOOL, no hint. set_all_tabs_in_front bare-assigns.
    all_tabs_in_front: v.boolean('all_tabs_in_front'),
    // tab_container.cpp:1214, plain BOOL, no hint. set_switch_on_drag_hover
    // delegates to TabBar's own, which bare-assigns (tab_bar.cpp:2022).
    switch_on_drag_hover: v.boolean('switch_on_drag_hover'),
    // tab_container.cpp:1215, plain BOOL, no hint. set_drag_to_rearrange_enabled
    // delegates to TabBar's own, which bare-assigns (tab_bar.cpp:1995).
    drag_to_rearrange_enabled: v.boolean('drag_to_rearrange_enabled'),
    // tab_container.cpp:1216, plain INT, PROPERTY_HINT_NONE. set_tabs_rearrange_group delegates
    // to TabBar's (tab_bar.cpp:2003), which assigns any integer. -1 disables cross-container rearranging.
    tabs_rearrange_group: v.int('tabs_rearrange_group'),
    // tab_container.cpp:1217, plain BOOL, no hint.
    // set_use_hidden_tabs_for_min_size bare-assigns.
    use_hidden_tabs_for_min_size: v.boolean('use_hidden_tabs_for_min_size'),
    // tab_container.cpp:1218, PROPERTY_HINT_ENUM "None,Click,All" (0-2). The internal TabBar's
    // Control::set_focus_mode, `ERR_FAIL_INDEX((int)p_focus_mode, 4)` (control.cpp:2267), also
    // accepts 3 (Accessibility), which the hint never lists, so 3 warns and does not error.
    tab_focus_mode: v.enumInt('tab_focus_mode', 0, 2, FOCUS_MODE_HINT_NAMES, {
        hinted: 'tab_container.cpp:1218',
    }),
    // tab_container.cpp:1219, plain BOOL, no hint. set_deselect_enabled
    // delegates to TabBar's own, which bare-assigns (tab_bar.cpp:2038).
    deselect_enabled: v.boolean('deselect_enabled'),
});
//# sourceMappingURL=linterParser.js.map